import {
    Component,
    DestroyRef,
    OnInit,
    ViewChild,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSortModule, Sort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { DateTime } from 'luxon';
import { UserInfo } from '../../services/user.service';
import { AppService } from '../../services/app.service';
import { SettingsService } from '../../services/settings.service';
import { UserService } from '../../services/user.service';
import { AppTable } from '../../classes/application-table';
import { Disqualify } from '../../classes/disqualify';
import { InputBoxDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs/inputbox';
import { WaitModalComponent } from '../modals/wait-modal/wait-modal.component';
import { ConfirmationDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs/confirmation';
import { UpdateFocus } from '../../classes/update-focus';
import { MatDividerModule } from '@angular/material/divider';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { SchoolYearPipe } from '../../pipes/schoolyear/schoolyear.pipe';
import { MatButtonModule } from '@angular/material/button';
import { SnackBarService } from '@polk_county_public_schools/ngx-pcps-dialogs/snackbar';
import { ErrorService } from '../../services/error.service';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
    selector: 'app-applicant-list',
    templateUrl: './applicant-list.component.html',
    styleUrls: ['./applicant-list.component.scss'],
    imports: [
        MatPaginatorModule,
        MatDividerModule,
        CommonModule,
        MatIconModule,
        RouterModule,
        MatSortModule,
        MatTableModule,
        FormsModule,
        MatInputModule,
        SchoolYearPipe,
        MatButtonModule,
        MatTooltipModule
    ],
    providers: [
        InputBoxDialogService
    ]
})
export class ApplicantListComponent implements OnInit {
    protected readonly destroyRef = inject(DestroyRef);
    protected readonly appSvc = inject(AppService);
    protected readonly confirmDlgSvc = inject(ConfirmationDialogService);
    protected readonly matDialog = inject(MatDialog);
    protected readonly settingSvc = inject(SettingsService);
    protected readonly snackBarSvc = inject(SnackBarService);
    protected readonly userSvc = inject(UserService);
    protected readonly errorSvc = inject(ErrorService);
    protected readonly inputDlgSvc = inject(InputBoxDialogService);
    user: UserInfo;
    dataSource: MatTableDataSource<AppTable>;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    displayedColumns: string[] = [
        'FinalizedDate',
        'StudentName',
        'SchoolName',
        'EmailUserName',
        'GPA',
        'SATCombined',
        'ACT',
        'CLT',
        'HoursCombined',
        'Status',
        'Action',
    ];
    pageSize: number;
    pageIndex: number;
    schoolYear: string;
    appStudentIDs: string[];
    input: string;
    sortActive: string;
    sortDirection: SortDirection;

    ngOnInit() {
        this.user = this.userSvc.User;
        this.getTableData();
        this.pageSize = parseInt(sessionStorage.getItem('pageSizeApp')) || 10;
        this.pageIndex = parseInt(sessionStorage.getItem('pageIndexApp')) || 0;
        this.input = sessionStorage.getItem('filterApp') || '';
        this.getSettings();
    }

    getSettings() {
        this.settingSvc.SettingsSubject.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((settings) => {
            if (settings == null) return;
            this.schoolYear = settings.find(
                (f) => f.Label === 'School year (scholarship admin)'
            ).Value;
        });
    }

    getTableData() {
        this.appSvc
            .GetApplicationsTable()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    for (let a of res) {
                        if (a.EmailUserName == null) {
                            a.EmailUserName = '';
                        }
                    }

                    this.dataSource = new MatTableDataSource(res);
                    this.dataSource.paginator = this.paginator;
                    this.dataSource.filter = this.input;
                    this.sortActive = sessionStorage.getItem('activeApp') || '';
                    this.sortDirection = sessionStorage.getItem('directionApp') as SortDirection || '';
                    this.sortChange({ active: this.sortActive, direction: this.sortDirection });
                    this.appStudentIDs = res.map(m => m.StudentID);
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    disqualify(appID: number) {
        let dialog = this.inputDlgSvc.Open({
            disableClose: true,
            TitleText: 'Are You Sure?',
            MessageText:
                'Please give a reason why you want to disqualify this applicant',
        });

        dialog.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
            if (res != null && res != '') {
                let dto: Disqualify = {
                    Id: appID,
                    Reason: res,
                };

                this.appSvc
                    .Disqualify(dto)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: (res) => {
                            for (let a of res) {
                                if (a.EmailUserName == null) {
                                    a.EmailUserName = '';
                                }
                            }

                            this.dataSource = new MatTableDataSource(res);
                            this.dataSource.paginator = this.paginator;
                            this.dataSource.filter = this.input;
                            this.sortChange({ active: this.sortActive, direction: this.sortDirection });
                            this.appStudentIDs = res.map(m => m.StudentID);
                        },
                        error: (error) => {
                            this.errorSvc.HandleServiceError(error);
                        }
                    });
            }
        });
    }

    undo(appID: number) {
        let dto: Disqualify = {
            Id: appID,
            Reason: null,
        };

        this.appSvc
            .Disqualify(dto)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    for (let a of res) {
                        if (a.EmailUserName == null) {
                            a.EmailUserName = '';
                        }
                    }

                    this.dataSource = new MatTableDataSource(res);
                    this.dataSource.paginator = this.paginator;
                    this.dataSource.filter = this.input;
                    this.sortChange({ active: this.sortActive, direction: this.sortDirection });
                    this.appStudentIDs = res.map(m => m.StudentID);
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();
        sessionStorage.setItem('filterApp', filterValue.trim().toLowerCase());

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    handlePageEvent(e: PageEvent) {
        this.pageSize = e.pageSize;
        sessionStorage.setItem('pageSizeApp', e.pageSize.toString());
        this.pageIndex = e.pageIndex;
        sessionStorage.setItem('pageIndexApp', e.pageIndex.toString());
    }

    sortChange(sort: Sort) {
        if (!sort.active || sort.direction === '') {
            this.dataSource.data = this.dataSource.data.slice();
            return;
        }

        sessionStorage.setItem('activeApp', sort.active);
        sessionStorage.setItem('directionApp', sort.direction);

        this.dataSource.data = this.dataSource.data.sort((a, b) => {
            const isAsc = sort.direction === 'asc';
            switch (sort.active) {
                case 'FinalizedDate':
                    return compare(a.FinalizedDate == null ? 0 : DateTime.fromISO(a.FinalizedDate).toMillis(), b.FinalizedDate == null ? 0 : DateTime.fromISO(b.FinalizedDate).toMillis(), isAsc);
                case 'StudentName':
                    return compare(a.StudentName.toLowerCase(), b.StudentName.toLowerCase(), isAsc);
                case 'SchoolName':
                    return compare(a.SchoolName.toLowerCase(), b.SchoolName.toLowerCase(), isAsc);
                case 'EmailUserName':
                    return compare(a.EmailUserName.toLowerCase(), b.EmailUserName.toLowerCase(), isAsc);
                case 'GPA':
                    return compare(+a.GPA, +b.GPA, isAsc);
                case 'SATCombined':
                    return compare(a.SATCombined, b.SATCombined, isAsc);
                case 'ACT':
                    return compare(a.ACT, b.ACT, isAsc);
                case 'CLT':
                    return compare(a.CLT, b.CLT, isAsc);
                case 'HoursCombined':
                    return compare(a.HoursCombined, b.HoursCombined, isAsc);
                case 'Status':
                    return compare(a.Status, b.Status, isAsc);
                default:
                    return 0;
            }
        });
    }

    downloadPDF(app: AppTable) {
        let wait = this.matDialog.open(WaitModalComponent);

        this.appSvc
            .ApplicationDetailsReport(false, app.AppID)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    wait.close();
                    this.saveBlobToFile(
                        res.body,
                        app.StudentFirstName +
                        '_' +
                        app.StudentLastName +
                        '_Application_' +
                        DateTime.now().toFormat('MMM_d_y')
                    );
                },
                error: (error) => {
                    wait.close();
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    saveBlobToFile(blob: Blob, fileName: string): void {
        //setup temporary in-memory URL and html tag
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);

        //programattically click on tag for user
        anchor.click();

        //delete tag and URL
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);
    }

    focusUpdate() {
        let dialog = this.confirmDlgSvc.Open({
            disableClose: true,
            TitleText: 'Are You Sure?',
            MessageText: `This will update all the applications with the lastest data from Focus.`,
            OKText: 'Update',
            CancelText: 'Cancel'
        });

        dialog.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
            if (res === true) {
                let wait = this.matDialog.open(WaitModalComponent);

                let dto: UpdateFocus = {
                    StudentIDs: this.appStudentIDs,
                };

                this.appSvc
                    .GetFocusData(dto)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: (res) => {
                            wait.close();
                            this.snackBarSvc.success(
                                'Data Updated',
                                5000
                            );
                        },
                        error: (error) => {
                            wait.close();
                            this.errorSvc.HandleServiceError(error);
                        }
                    });
            }
        });
    }
}

function compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}